import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { getApiOrigin } from '../services/api';
import GalerieImagesProduit from '../composants/GalerieImagesProduit';
import SelectionVariante from '../composants/SelectionVariante';
import { usePanier } from '../contexte/PanierContexte';
import { formaterPrix } from '../services/format';

export default function DetailsProduit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { ajouter } = usePanier();

  const [produit, setProduit] = useState(null);
  const [varianteId, setVarianteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [produitsSimilaires, setProduitsSimilaires] = useState([]);
  const [loadingSimilaires, setLoadingSimilaires] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function charger() {
      setLoading(true);
      try {
        const { data } = await api.get(`/produits/${id}`);
        if (!mounted) return;
        setProduit(data);
        const v = data?.variantes?.[0]?.id || null;
        setVarianteId(v);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    charger();
    return () => {
      mounted = false;
    };
  }, [id]);

  const variantes = useMemo(() => produit?.variantes || [], [produit]);
  const variante = useMemo(() => variantes.find((v) => v.id === varianteId) || null, [variantes, varianteId]);

  function normaliserUrlImage(url) {
    if (!url) return null;
    const s = String(url);
    if (s.startsWith('http://') || s.startsWith('https://')) return s;
    const origin = getApiOrigin().replace(/\/$/, '');
    if (s.startsWith('/uploads')) return `${origin}${s}`;
    if (s.startsWith('uploads/')) return `${origin}/${s}`;
    return s;
  }

  useEffect(() => {
    let mounted = true;

    async function chargerSimilaires() {
      const categorie_id = Number(produit?.categorie_id || produit?.categorie?.id || 0);
      if (!categorie_id || !produit?.id) {
        setProduitsSimilaires([]);
        return;
      }

      setLoadingSimilaires(true);
      try {
        const { data } = await api.get('/produits', { params: { categorie_id } });
        if (!mounted) return;
        const items = (Array.isArray(data) ? data : []).filter((p) => Number(p?.id) !== Number(produit.id));
        setProduitsSimilaires(items);
      } finally {
        if (mounted) setLoadingSimilaires(false);
      }
    }

    chargerSimilaires();
    return () => {
      mounted = false;
    };
  }, [produit?.id, produit?.categorie_id, produit?.categorie?.id]);

  function ajouterAuPanier() {
    if (!produit || !variante) return;

    ajouter({
      produit_id: produit.id,
      variante_id: variante.id,
      nom: produit.nom,
      variante_libelle: `${variante.nom_variante} : ${variante.valeur_variante}`,
      prix_unitaire: Number(variante.prix),
      image_url: normaliserUrlImage(produit?.images?.[0]?.url_image || null),
      quantite: 1
    });

    navigate('/panier');
  }

  if (loading) {
    return <div className="rounded-3xl border border-gray-200 bg-white p-8 text-sm text-gray-600">Chargement...</div>;
  }

  if (!produit) {
    return <div className="rounded-3xl border border-gray-200 bg-white p-8 text-sm text-gray-600">Produit introuvable</div>;
  }

  return (
    <div className="grid gap-12">
      <div className="grid gap-8 md:grid-cols-2">
        <GalerieImagesProduit images={produit.images || []} />

        <div className="grid gap-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-marega-200">{produit.categorie?.nom || 'Produit'}</div>
            <div className="mt-2 text-3xl font-bold text-gray-900">{produit.nom}</div>
            <div className="mt-3 text-sm text-gray-600">{produit.description || ''}</div>
          </div>

          <div className="rounded-3xl border border-gray-200 bg-white p-6">
            <SelectionVariante variantes={variantes} value={varianteId} onChange={setVarianteId} />

            <div className="mt-6 flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-600">Prix</div>
                <div className="text-2xl font-extrabold text-gray-900">{formaterPrix(variante?.prix || 0)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-600">Stock</div>
                <div className="text-sm font-semibold text-gray-900">{variante?.stock ?? 0}</div>
              </div>
            </div>

            <button
              onClick={ajouterAuPanier}
              disabled={!variante || Number(variante.stock) <= 0}
              className="mt-6 w-full rounded-2xl bg-marega-600 px-5 py-4 text-sm font-semibold text-white shadow-lg shadow-marega-600/20 hover:bg-marega-500 disabled:opacity-50"
            >
              Ajouter au panier
            </button>
          </div>
        </div>
      </div>

      <section className="grid gap-5">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-gray-600">Suggestions</div>
            <div className="mt-2 text-xl font-bold text-gray-900">Articles de la même catégorie</div>
          </div>
        </div>

        {loadingSimilaires ? (
          <div className="rounded-3xl border border-gray-200 bg-white p-6 text-sm text-gray-600">Chargement...</div>
        ) : produitsSimilaires.length ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {produitsSimilaires.map((p) => {
              const image = p?.images?.[0]?.url_image || p?.images_produit?.[0]?.url_image || null;
              const src = normaliserUrlImage(image);
              const vars = p?.variantes || p?.variantes_produit || [];
              const prixMin = vars.length ? Math.min(...vars.map((v) => Number(v?.prix || 0))) : 0;

              return (
                <button
                  key={p.id}
                  onClick={() => navigate(`/produits/${p.id}`)}
                  className="text-left overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md"
                >
                  <div className="aspect-square overflow-hidden bg-gray-100">
                    {src ? (
                      <img src={src} alt={p.nom} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-xs text-gray-500">Aucune image</div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="line-clamp-1 text-sm font-semibold text-gray-900">{p.nom}</div>
                    <div className="mt-2 text-sm font-bold text-orange-500">{formaterPrix(prixMin)}</div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="rounded-3xl border border-gray-200 bg-white p-6 text-sm text-gray-600">Aucun article similaire.</div>
        )}
      </section>
    </div>
  );
}
